import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Lock, User, CheckCircle, AlertCircle } from 'lucide-react';
import { usePlatformBranding } from '../hooks/usePlatformBranding';

const ActivateAccount = () => {
  const branding = usePlatformBranding();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending'); // pending, success, error
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    // Manejar la invitación de Supabase Auth
    const handleAuthCallback = async () => {
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const type = searchParams.get('type');

      if (type === 'invite' && accessToken && refreshToken) {
        try {
          // Establecer la sesión con los tokens de la URL
          const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (sessionError) throw sessionError;

          if (sessionData.user) {
            setUserEmail(sessionData.user.email);
            setStatus('pending');
            setMessage('');
          }
        } catch (error) {
          setStatus('error');
          setMessage('Error al procesar la invitación. El enlace puede haber expirado.');
        }
      } else {
        setStatus('error');
        setMessage('Enlace de activación inválido o expirado');
      }
      setLoading(false);
    };

    handleAuthCallback();
  }, [searchParams]);

  const buildPasswordError = (message) => {
    const lower = (message || '').toLowerCase();
    if (lower.includes('password')) {
      if (lower.includes('least') && lower.includes('characters')) {
        return 'La contraseña es demasiado corta.';
      }
      if (lower.includes('weak')) {
        return 'La contraseña es débil. Usa una combinación de letras, números y símbolos.';
      }
      if (lower.includes('uppercase') || lower.includes('lowercase') || lower.includes('number') || lower.includes('symbol')) {
        return 'La contraseña debe incluir mayúsculas, minúsculas, números y símbolos.';
      }
      return 'La contraseña no cumple con las reglas de seguridad.';
    }
    return null;
  };

  const handleActivation = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (formData.password !== formData.confirmPassword) {
      setMessage('Las contraseñas no coinciden.');
      return;
    }

    if (formData.password.length < 6) {
      setMessage('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      // Actualizar la contraseña del usuario usando Supabase Auth
      const { data: updateData, error: updateError } = await supabase.auth.updateUser({
        password: formData.password
      });

      if (updateError) throw updateError;

      // Obtener los metadatos del usuario para vincular con el coach
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user && user.user_metadata?.coach_id) {
        // Marcar el coach como usuario activo
        const { error: coachUpdateError } = await supabase
          .from('coaches')
          .update({ 
            usuario_creado: true,
            auth_user_id: user.id 
          })
          .eq('id', user.user_metadata.coach_id);

        if (coachUpdateError) {
          // Silent coach update error
        }
      }

      setStatus('success');
      setMessage('¡Cuenta activada exitosamente! Redirigiendo a la aplicación...');
      
      // Redirigir a groups después de 3 segundos
      setTimeout(() => {
        navigate('/groups');
      }, 3000);

    } catch (error) {
      setStatus('error');
      
      let errorMessage = 'Error al activar la cuenta';
      const friendly = buildPasswordError(error?.message);
      if (friendly) {
        errorMessage = friendly;
      } else if (error.message.includes('expired')) {
        errorMessage = 'El enlace de activación ha expirado';
      } else if (error.message.includes('invalid')) {
        errorMessage = 'El enlace de activación es inválido';
      }
      
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="bg-gradient-to-b from-gray-900 to-black p-8 rounded-lg shadow-2xl w-full max-w-md border border-gray-600">
        <div className="text-center mb-8">
          <img src={branding.logo} alt={branding.name} className="h-12 mx-auto mb-4 object-contain" />
          <h1 className="text-2xl font-bold text-white mb-2">Activar cuenta</h1>
          <p className="text-gray-400">Crea tu contraseña para acceder</p>
          {userEmail && (
            <p className="text-gray-500 text-sm mt-2">
              Activando cuenta para: {userEmail}
            </p>
          )}
        </div>

        {loading && status === 'pending' && (
          <div className="text-center py-8">
            <div className="text-white font-medium mb-2">Procesando invitación...</div>
            <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-red-900 border border-red-600 rounded-lg p-4 mb-6 flex items-center">
            <AlertCircle className="text-red-200 mr-3" size={20} />
            <p className="text-red-200">{message}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-green-900 border border-green-600 rounded-lg p-4 mb-6 flex items-center">
            <CheckCircle className="text-green-200 mr-3" size={20} />
            <div>
              <p className="text-green-200 font-medium">{message}</p>
              <p className="text-green-300 text-sm mt-1">
                Serás redirigido automáticamente...
              </p>
            </div>
          </div>
        )}

        {status === 'pending' && !loading && (
          <>
            <div className="bg-gray-800 border border-gray-600 rounded-lg p-4 mb-6">
              <h3 className="text-white font-medium mb-2 flex items-center">
                <User className="mr-2" size={18} />
                Bienvenido Coach
              </h3>
              <p className="text-gray-400 text-sm">
                Por favor, crea una contraseña para tu cuenta. 
                Podrás usarla para iniciar sesión en la aplicación de asistencia.
              </p>
            </div>

            <form onSubmit={handleActivation} className="space-y-6">
              <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
                <p className="text-gray-200 font-medium mb-1">Reglas de contraseña</p>
                <ul className="text-gray-400 text-sm list-disc pl-5 space-y-1">
                  <li>Mínimo 6 caracteres.</li>
                  <li>Si el sistema rechaza la contraseña, usa 8+ caracteres con mayúsculas, minúsculas, número y símbolo.</li>
                </ul>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-black border border-gray-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-gray-400"
                    placeholder="Mínimo 6 caracteres"
                    required
                    disabled={loading}
                    minLength={6}
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Confirmar Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-black border border-gray-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-gray-400"
                    placeholder="Repite la contraseña"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {message && status === 'pending' && (
                <p className="text-red-400 text-sm">{message}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full font-bold py-3 px-6 rounded-lg transition-all duration-200 border ${
                  loading 
                    ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-gray-600 to-gray-500 hover:from-gray-500 hover:to-gray-400 text-white border-gray-400'
                }`}
              >
                {loading ? 'Activando cuenta...' : 'Activar Cuenta'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                Complete el proceso para activar su cuenta
              </p>
            </div>
          </>
        )}

        {status !== 'pending' && (
          <div className="mt-6 text-center">
            <a 
              href="/login" 
              className="text-gray-400 hover:text-white text-sm"
            >
              Ir al inicio de sesión
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivateAccount;




